import { inject, Injectable } from '@angular/core';

import { getOpenTelemetryProviderAsync } from '../open-telemetry.facade';
import { OpenTelemetryConfigFactory } from '../config/open-telemetry.config.factory';
import type { OtelcolConfig } from '../config/open-telemetry.interfaces';
import type {
  Attributes,
  AttributeValue,
  Span,
  SpanContext,
  OtelApiModule,
  OtelTracer,
  OtelProvider,
  OtelContext,
  OtelTraceApi,
  ExtendedSpan,
} from '../open-telemetry.interfaces';
import type { PerformanceMetric } from './performance-metrics.interfaces';
import { PerformanceMetricCategory } from './performance-metrics.interfaces';
import { CustomIdGenerator } from '../open-telemetry.span';
import type { PerformanceMetricsBufferService } from './performance-metrics-buffer.service';

export interface MetricSpanResult {
  span: Span | null;
  traceId?: string;
}

export interface RootMetricOptions {
  traceId?: string;
  startTime?: number;
  endTime?: number;
}

export interface ChildMetricOptions extends RootMetricOptions {
  inheritParentTraceId?: boolean;
}

interface ScreenSpanState {
  span: Span | null;
}

@Injectable({ providedIn: 'root' })
export class PerformanceMetricsSpanManager {
  private readonly openTelemetryConfigFactory: OpenTelemetryConfigFactory = inject(OpenTelemetryConfigFactory);
  private readonly idGenerator: CustomIdGenerator = new CustomIdGenerator();
  private bufferService: PerformanceMetricsBufferService | null = null;

  private readonly screenSpans: Map<string, ScreenSpanState> = new Map<string, ScreenSpanState>();

  private appStartSpan: Span | null = null;
  private appStartMetric: PerformanceMetric | null = null;
  private appStartTraceId: string | null = null;
  private appStartSpanId: string | null = null;
  private appStartTime: number | null = null; // Время начала приложения в миллисекундах относительно timeOrigin
  private currentParentSpan: Span | null = null;
  private currentScreenName: string | null = null;
  private currentScreenSpanId: string | null = null;
  private otelApiPromise: Promise<OtelApiModule> | null = null;
  private appStartFinalizePending: boolean = false;
  private fallbackParentContext: SpanContext | null = null;

  public hasParentReference(): boolean {
    return Boolean(
      this.currentParentSpan ||
        this.appStartSpan ||
        (this.currentScreenName && this.screenSpans.get(this.currentScreenName)?.span) ||
        this.fallbackParentContext ||
        this.appStartSpanId ||
        this.currentScreenSpanId
    );
  }

  /**
   * Генерирует и сохраняет ID для app_start спана сразу, до его создания
   * Это позволяет дочерним спанам использовать эти ID для установки родителя
   * Также сохраняет время начала приложения для синхронизации времени спанов
   */
  public initializeAppStartIds(appStartTime?: number): { traceId: string; spanId: string } {
    if (!this.appStartTraceId || !this.appStartSpanId) {
      this.appStartTraceId = this.idGenerator.generateTraceId();
      this.appStartSpanId = this.idGenerator.generateSpanId();
    }

    if (appStartTime !== undefined && this.appStartTime === null) {
      const timeOrigin: number = performance.timeOrigin ?? Date.now() - performance.now();
      this.appStartTime = timeOrigin + appStartTime;
    }

    return {
      traceId: this.appStartTraceId,
      spanId: this.appStartSpanId,
    };
  }

  /**
   * Получает время начала приложения для синхронизации времени дочерних спанов
   */
  public getAppStartTime(): number | null {
    return this.appStartTime;
  }

  /**
   * Генерирует и сохраняет ID для screen_render спана сразу, до его создания
   * Это позволяет дочерним спанам использовать этот ID для установки родителя
   */
  public initializeScreenSpanId(screenName: string): string {
    if (!this.currentScreenSpanId || this.currentScreenName !== screenName) {
      this.currentScreenSpanId = this.idGenerator.generateSpanId();
    }
    return this.currentScreenSpanId;
  }

  /**
   * Синхронно устанавливает currentScreenName ДО создания спана экрана
   * Это критически важно - сетевые запросы могут создаваться параллельно и должны видеть текущий экран
   */
  public setCurrentScreenName(screenName: string): void {
    this.currentScreenName = screenName;
  }

  public async beginAppStart(metric: PerformanceMetric): Promise<MetricSpanResult> {
    const previousAppStartSpan: Span | null = this.appStartSpan;

    if (!this.appStartTraceId || !this.appStartSpanId) {
      this.initializeAppStartIds();
    }

    const spanOptions: { startTime?: number } = {};
    if (this.appStartTime !== null) {
      spanOptions.startTime = this.appStartTime;
    }

    const span: Span | null = await this.startSpan(metric, null, spanOptions);
    if (!span) {
      return { span: null };
    }

    if (this.appStartTraceId && this.appStartSpanId) {
      try {
        await this.setSpanContextFromIds(span, this.appStartTraceId, this.appStartSpanId);
      } catch (error) {
        console.warn('[Performance Metrics]: Failed to set app start span context from IDs', error);
      }
    }

    this.validateRootSpanIndependence(span, 'app_start.cold_start', this.appStartTraceId || undefined);

    if (previousAppStartSpan) {
      this.storeFallbackParentContext(previousAppStartSpan);
      this.endSpan(previousAppStartSpan);
    }

    this.appStartSpan = span;
    this.appStartMetric = metric;

    const traceId: string | undefined = this.setTraceIdAttribute(span);

    if (this.appStartFinalizePending) {
      this.appStartFinalizePending = false;
      this.finalizeAppStartIfNeeded();
    }

    return { span, traceId: this.appStartTraceId || traceId };
  }

  public async beginScreen(screenName: string, metric: PerformanceMetric): Promise<MetricSpanResult> {
    if (!this.currentScreenSpanId || this.currentScreenName !== screenName) {
      this.initializeScreenSpanId(screenName);
    }

    const normalizedScreenName: string = screenName;
    const previousScreenName: string | null = this.currentScreenName;
    const previousScreenState: ScreenSpanState | undefined = previousScreenName
      ? this.screenSpans.get(previousScreenName)
      : undefined;

    const existingScreenState: ScreenSpanState | undefined = this.screenSpans.get(normalizedScreenName);
    if (existingScreenState?.span) {
      this.currentScreenName = normalizedScreenName;
      this.currentParentSpan = existingScreenState.span;
      const spanContext: SpanContext = existingScreenState.span.spanContext();
      this.currentScreenSpanId = spanContext.spanId;
      const traceId: string | undefined = this.setTraceIdAttribute(existingScreenState.span);
      return { span: existingScreenState.span, traceId };
    }

    // Критически важно: устанавливаем currentScreenName СРАЗУ, до создания спана
    // Сетевые запросы могут создаваться параллельно и должны видеть текущий экран
    this.currentScreenName = normalizedScreenName;

    let parentReference: Span | SpanContext | null = this.appStartSpan ?? this.fallbackParentContext;

    if (!parentReference && this.appStartTraceId && this.appStartSpanId) {
      parentReference = await this.createSpanContextFromIds(this.appStartTraceId, this.appStartSpanId);
    }

    if (!parentReference) {
      console.warn('[Performance Metrics]: No parent reference for screen span, creating root span');
    }

    const span: Span | null = await this.startSpan(metric, parentReference);

    // Критически важно: устанавливаем currentParentSpan СРАЗУ после создания span
    // Параллельные сетевые запросы должны видеть правильный родитель
    if (span) {
      const spanContext: SpanContext = span.spanContext();
      this.currentScreenSpanId = spanContext.spanId;
      this.currentParentSpan = span;
      this.screenSpans.set(normalizedScreenName, { span });

      if (this.bufferService) {
        queueMicrotask(() => {
          this.bufferService?.processBufferedNetworkTrackings().catch((error: unknown) => {
            console.warn('[Performance Metrics]: Failed to process buffered network trackings', error);
          });
        });
      }
    } else {
      this.screenSpans.set(normalizedScreenName, { span: null });
    }

    const traceId: string | undefined = this.setTraceIdAttribute(span);

    if (previousScreenName && previousScreenName !== normalizedScreenName) {
      if (previousScreenState?.span && previousScreenState.span !== span) {
        this.storeFallbackParentContext(previousScreenState.span);
        this.endSpan(previousScreenState.span);
      }
      this.screenSpans.delete(previousScreenName);
      if (this.currentParentSpan === previousScreenState?.span) {
        this.currentParentSpan = null;
      }
    }

    return { span, traceId };
  }

  public async finalizeScreen(
    screenName: string,
    metric: PerformanceMetric,
    eventAttributes: { duration: number; blockingRequestCount: number }
  ): Promise<void> {
    const existingSpan: Span | null = this.screenSpans.get(screenName)?.span ?? null;

    if (existingSpan) {
      const attributesToApply: Attributes = this.buildSpanAttributes(metric);
      this.applyAttributes(existingSpan, attributesToApply);
      const traceId: string | undefined = this.extractTraceIdFromAttributes(metric.attributes);
      this.setTraceIdAttribute(existingSpan, traceId);

      existingSpan.addEvent?.('screen.render.ready', {
        duration: eventAttributes.duration,
        blocking_request_count: eventAttributes.blockingRequestCount,
      });
      // Критически важно: сохраняем контекст ДО завершения спана
      // Дочерние спаны могут создаваться после завершения экрана и должны находить родителя
      this.storeFallbackParentContext(existingSpan);

      this.endSpan(existingSpan);

      // Не удаляем спан из screenSpans сразу после завершения - дочерние спаны могут создаваться позже
      if (this.currentScreenName !== screenName) {
        this.screenSpans.delete(screenName);
        if (this.currentParentSpan === existingSpan) {
          this.currentParentSpan = null;
        }
      }

      this.finalizeAppStartIfNeeded();
      return;
    }

    const fallbackSpan: Span | null = await this.startSpan(metric, await this.resolveParentReference());

    if (!fallbackSpan) {
      return;
    }

    const traceId: string | undefined = this.extractTraceIdFromAttributes(metric.attributes);
    this.setTraceIdAttribute(fallbackSpan, traceId);

    fallbackSpan.addEvent?.('screen.render.ready', {
      duration: eventAttributes.duration,
      blocking_request_count: eventAttributes.blockingRequestCount,
    });

    this.storeFallbackParentContext(fallbackSpan);
    this.endSpan(fallbackSpan);
    this.finalizeAppStartIfNeeded();
  }

  /**
   * Создает и завершает root спан в полностью изолированном контексте.
   * Это предотвращает влияние root span на активный контекст для других спанов и гарантирует,
   * что root spans не будут связаны друг с другом через контекст.
   */
  private async createAndFinalizeSpanInIsolatedContext(
    metric: PerformanceMetric,
    options: RootMetricOptions = {}
  ): Promise<void> {
    const spanName: string = `${metric.category}.${metric.event}`;

    if (!metric.category || !metric.event) {
      console.warn('[Performance Metrics] createAndFinalizeSpanInIsolatedContext: Invalid metric structure', metric);
      return;
    }

    try {
      const span: Span | null = await this.startRootSpanInIsolatedContext(metric, {
        startTime: options.startTime,
      });

      if (!span) {
        console.warn('[Performance Metrics] createAndFinalizeSpanInIsolatedContext: Failed to create span', {
          spanName,
        });
        return;
      }

      const traceId: string | undefined = options.traceId || this.extractTraceIdFromAttributes(metric.attributes);

      // Важно: устанавливаем traceId в span context для правильной отправки root span в collector
      if (traceId) {
        try {
          await this.setSpanContextTraceId(span, traceId);
        } catch (error: unknown) {
          console.warn(
            '[Performance Metrics] createAndFinalizeSpanInIsolatedContext: Failed to set traceId in span context',
            {
              spanName,
              error,
            }
          );
        }
      }

      this.validateRootSpanIndependence(span, spanName, traceId);

      this.setTraceIdAttribute(span, traceId);
      this.endSpan(span, options.endTime);
    } catch (error: unknown) {
      console.warn('[Performance Metrics] createAndFinalizeSpanInIsolatedContext: Failed to create and finalize span', {
        spanName,
        error,
      });
      try {
        await this.createAndFinalizeSpan(metric, null, options);
      } catch (fallbackError: unknown) {
        console.warn('[Performance Metrics] createAndFinalizeSpanInIsolatedContext: Fallback also failed', {
          spanName,
          error: fallbackError,
        });
      }
    }
  }

  /**
   * Создает и завершает спан для метрики производительности.
   * Общая логика для root и child спанов.
   */
  private async createAndFinalizeSpan(
    metric: PerformanceMetric,
    parentReference: Span | SpanContext | null,
    options: RootMetricOptions | ChildMetricOptions = {}
  ): Promise<void> {
    const spanName: string = `${metric.category}.${metric.event}`;
    const isRootSpan: boolean = !parentReference;

    if (!metric.category || !metric.event) {
      console.warn('[Performance Metrics]: Invalid metric structure', metric);
      return;
    }

    try {
      const span: Span | null = await this.startSpan(metric, parentReference, {
        startTime: options.startTime,
      });

      if (!span) {
        console.warn('[Performance Metrics] createAndFinalizeSpan: Failed to create span', {
          spanName,
          isRootSpan,
        });
        return;
      }

      const traceId: string | undefined =
        options.traceId ||
        ('inheritParentTraceId' in options && options.inheritParentTraceId && parentReference
          ? this.extractTraceIdFromReference(parentReference)
          : undefined) ||
        this.extractTraceIdFromAttributes(metric.attributes);

      // Важно: для root spans устанавливаем traceId в span context для правильной отправки в collector
      if (!parentReference && traceId) {
        try {
          await this.setSpanContextTraceId(span, traceId);
        } catch (error: unknown) {
          console.warn('[Performance Metrics]: Failed to set traceId in span context', error);
        }
      }

      this.setTraceIdAttribute(span, traceId);
      this.endSpan(span, options.endTime);
    } catch (error: unknown) {
      console.warn('[Performance Metrics]: Failed to create and finalize span', {
        spanName,
        isRootSpan,
        error,
      });
    }
  }

  public async recordChildMetric(metric: PerformanceMetric, options: ChildMetricOptions = {}): Promise<void> {
    const parentReference: Span | SpanContext | null = await this.resolveParentReference();
    await this.createAndFinalizeSpan(metric, parentReference, options);
  }

  public async recordRootMetric(metric: PerformanceMetric, options: RootMetricOptions = {}): Promise<void> {
    await this.createAndFinalizeSpanInIsolatedContext(metric, options);
  }

  public async getActiveTraceId(): Promise<string | undefined> {
    const parentSpan: Span | null = await this.resolveActiveParentSpan();
    return parentSpan?.spanContext().traceId;
  }

  public getCurrentScreenName(): string | null {
    return this.currentScreenName;
  }

  public hasScreenSpan(screenName: string): boolean {
    const screenSpan: ScreenSpanState | undefined = this.screenSpans.get(screenName);
    return !!screenSpan?.span;
  }

  public setBufferService(bufferService: PerformanceMetricsBufferService): void {
    this.bufferService = bufferService;
  }

  public updateAppStartAttributes(attributes: PerformanceMetric['attributes']): void {
    if (!this.appStartMetric) {
      return;
    }

    this.appStartMetric = {
      ...this.appStartMetric,
      attributes,
    };
  }

  private async resolveParentReference(): Promise<Span | SpanContext | null> {
    if (this.currentParentSpan) {
      return this.currentParentSpan;
    }

    if (this.currentScreenName) {
      const screenSpan: ScreenSpanState | undefined = this.screenSpans.get(this.currentScreenName);
      if (screenSpan?.span) {
        return screenSpan.span;
      }
    }

    if (this.appStartSpan) {
      return this.appStartSpan;
    }

    if (this.appStartTraceId && this.appStartSpanId) {
      return await this.createSpanContextFromIds(this.appStartTraceId, this.appStartSpanId);
    }

    // Критически важно: используем fallback контекст, чтобы дочерние спаны всегда имели родителя
    if (this.fallbackParentContext) {
      return this.fallbackParentContext;
    }

    return null;
  }

  private async resolveActiveParentSpan(): Promise<Span | null> {
    return this.extractSpanFromReference(await this.resolveParentReference());
  }

  private endSpan(span: Span | null, endTime?: number): void {
    if (!span) {
      console.warn('[Performance Metrics] endSpan: Span is null');
      return;
    }

    try {
      const extendedSpan: ExtendedSpan = span as ExtendedSpan;
      if (endTime !== undefined && extendedSpan.endTimeUnixNano !== undefined) {
        extendedSpan.endTimeUnixNano = Math.floor(endTime * 1_000_000);
      }
      span.end(endTime);
    } catch (error: unknown) {
      console.warn('[Performance Metrics]: failed to end span', { error });
    }
  }

  private extractSpanFromReference(reference: Span | SpanContext | null): Span | null {
    if (!reference) {
      return null;
    }

    return 'spanContext' in reference ? (reference as Span) : null;
  }

  private extractTraceIdFromReference(reference: Span | SpanContext | null): string | undefined {
    if (!reference) {
      return undefined;
    }

    if ('spanContext' in reference) {
      return (reference as Span).spanContext().traceId;
    }

    return (reference as SpanContext).traceId;
  }

  private extractTraceIdFromAttributes(attributes: PerformanceMetric['attributes']): string | undefined {
    if ('trace_id' in attributes && typeof attributes.trace_id === 'string') {
      return attributes.trace_id;
    }
    return undefined;
  }

  private setTraceIdAttribute(span: Span | null, providedTraceId?: string): string | undefined {
    if (!span) {
      return providedTraceId;
    }

    const spanTraceId: string = span.spanContext().traceId;
    const effectiveTraceId: string = providedTraceId || spanTraceId;

    if (effectiveTraceId) {
      span.setAttribute('trace_id', effectiveTraceId);
    }

    return effectiveTraceId;
  }

  public async startSpanForMetric(
    metric: PerformanceMetric,
    spanOptions?: { startTime?: number }
  ): Promise<Span | null> {
    const parentReference: Span | SpanContext | null = await this.resolveParentReference();

    // Синхронизируем время начала дочерних спанов с временем начала приложения
    const adjustedSpanOptions: { startTime?: number } = { ...spanOptions };
    if (adjustedSpanOptions.startTime !== undefined && this.appStartTime !== null) {
      if (adjustedSpanOptions.startTime < this.appStartTime) {
        adjustedSpanOptions.startTime = this.appStartTime;
      }
    }

    return await this.startSpan(metric, parentReference, adjustedSpanOptions);
  }

  private async startRootSpanInIsolatedContext(
    metric: PerformanceMetric,
    spanOptions?: { startTime?: number }
  ): Promise<Span | null> {
    const spanName: string = this.resolveSpanName(metric);

    const tracer: OtelTracer | null = await this.getTracer();

    if (!tracer) {
      console.warn('[Performance Metrics] startRootSpanInIsolatedContext: Tracer not available', { spanName });
      return null;
    }

    const spanOptionsForTracer: { kind: number; startTime?: number } = { kind: 1 };

    if (spanOptions?.startTime !== undefined) {
      spanOptionsForTracer.startTime = spanOptions.startTime;
    }

    // Создаем root span БЕЗ использования activeContext - это гарантирует изоляцию от других спанов
    const span: Span = tracer.startSpan(spanName, spanOptionsForTracer);

    const extendedSpan: ExtendedSpan = span as ExtendedSpan;
    if (spanOptions?.startTime !== undefined && extendedSpan.startTimeUnixNano !== undefined) {
      extendedSpan.startTimeUnixNano = Math.floor(spanOptions.startTime * 1_000_000);
    }

    span.setAttributes(this.buildSpanAttributes(metric));

    return span;
  }

  private async startSpan(
    metric: PerformanceMetric,
    parentReference?: Span | SpanContext | null,
    spanOptions?: { startTime?: number }
  ): Promise<Span | null> {
    const spanName: string = this.resolveSpanName(metric);

    const tracer: OtelTracer | null = await this.getTracer();

    if (!tracer) {
      console.warn('[Performance Metrics] startSpan: Tracer not available', { spanName });
      return null;
    }

    const spanOptionsForTracer: { kind: number; startTime?: number } = { kind: 1 };

    if (spanOptions?.startTime !== undefined) {
      spanOptionsForTracer.startTime = spanOptions.startTime;
    }

    const parentContext: OtelContext | undefined = await this.getParentContext(parentReference);

    // Используем context.with() для установки контекста - это гарантирует правильную parent-child связь
    const otelApi: OtelApiModule = await this.getOtelApi();
    const contextApi: OtelContext | undefined = otelApi.context;

    let span: Span;
    if (parentContext) {
      // Комбинируем context.with() и передачу контекста как третьего параметра для надежности
      if (contextApi?.with) {
        span = contextApi.with(parentContext, () => {
          return tracer.startSpan(spanName, spanOptionsForTracer, parentContext);
        });
      } else {
        span = tracer.startSpan(spanName, spanOptionsForTracer, parentContext);
      }
    } else {
      // Создаем root span без контекста для изоляции от других спанов
      span = tracer.startSpan(spanName, spanOptionsForTracer);
    }

    const extendedSpan: ExtendedSpan = span as ExtendedSpan;
    if (spanOptions?.startTime !== undefined && extendedSpan.startTimeUnixNano !== undefined) {
      extendedSpan.startTimeUnixNano = Math.floor(spanOptions.startTime * 1_000_000);
    }

    span.setAttributes(this.buildSpanAttributes(metric));

    return span;
  }

  private resolveSpanName(metric: PerformanceMetric): string {
    if (metric.category === PerformanceMetricCategory.IMAGE_LOAD) {
      return metric.category;
    }

    return `${metric.category}.${metric.event}`;
  }

  private async getTracer(): Promise<OtelTracer | null> {
    const provider: OtelProvider | null = await getOpenTelemetryProviderAsync();

    if (!provider) {
      console.error('[Performance Metrics]: provider not available');
      return null;
    }

    const otelConfig: OtelcolConfig = this.openTelemetryConfigFactory.get();
    return provider.getTracer(otelConfig.commonConfig.serviceName);
  }

  private async getParentContext(parentReference?: Span | SpanContext | null): Promise<OtelContext | undefined> {
    const otelApi: OtelApiModule = await this.getOtelApi();
    const traceApi: OtelTraceApi = otelApi.trace;
    const contextApi: OtelContext | undefined = otelApi.context;
    const rootContext: OtelContext = otelApi.ROOT_CONTEXT;

    if (!traceApi) {
      return undefined;
    }

    if (!parentReference) {
      return undefined;
    }

    // Используем activeContext как базовый контекст (setSpan не работает с rootContext)
    const activeContext: OtelContext | undefined = contextApi?.active?.() ?? rootContext;
    const baseContext: OtelContext | undefined = activeContext ?? rootContext;

    if (!baseContext) {
      return undefined;
    }

    if (typeof (parentReference as Span).spanContext === 'function') {
      const span: Span = parentReference as Span;
      const spanContext: SpanContext = span.spanContext();

      if (typeof traceApi.setSpanContext === 'function') {
        try {
          const contextWithParent: OtelContext | undefined = traceApi.setSpanContext(baseContext, spanContext);
          return contextWithParent;
        } catch (error: unknown) {
          console.warn('[Performance Metrics]: Failed to set parent span context', error);
          return undefined;
        }
      }
    }

    if (typeof traceApi.setSpanContext === 'function') {
      const spanContext: SpanContext = parentReference as SpanContext;
      try {
        const contextWithParent: OtelContext | undefined = traceApi.setSpanContext(baseContext, spanContext);
        return contextWithParent;
      } catch (error: unknown) {
        console.warn('[Performance Metrics]: Failed to set parent span context', error);
        return undefined;
      }
    }

    return undefined;
  }

  private async getOtelApi(): Promise<OtelApiModule> {
    if (!this.otelApiPromise) {
      this.otelApiPromise = import('@opentelemetry/api').then((module: unknown) => {
        const resolved: unknown =
          (module as { default?: unknown; trace?: OtelTraceApi; context?: OtelContext })?.default ?? module;
        const apiModule: { trace?: OtelTraceApi; context?: OtelContext; ROOT_CONTEXT?: OtelContext } = resolved as {
          trace?: OtelTraceApi;
          context?: OtelContext;
          ROOT_CONTEXT?: OtelContext;
        };
        const rootContext: OtelContext =
          apiModule.context?.ROOT_CONTEXT ?? apiModule.ROOT_CONTEXT ?? (apiModule.context as OtelContext);
        return {
          trace: apiModule.trace as OtelTraceApi,
          context: apiModule.context as OtelContext,
          ROOT_CONTEXT: rootContext,
        } as OtelApiModule;
      });
    }

    return this.otelApiPromise;
  }

  private buildSpanAttributes(metric: PerformanceMetric): Attributes {
    const rawAttributes: Record<string, unknown> = {
      ...metric.attributes,
      'metric.category': metric.category,
      'metric.event': metric.event,
    };

    return Object.entries(rawAttributes).reduce((acc: Attributes, [key, value]: [string, unknown]) => {
      if (value !== undefined && value !== null) {
        acc[key] = value as AttributeValue;
      }
      return acc;
    }, {} as Attributes);
  }

  private applyAttributes(span: Span, attributes: Attributes): void {
    Object.entries(attributes).forEach(([key, value]: [string, unknown]) => {
      if (value !== undefined && value !== null) {
        span.setAttribute(key, value as AttributeValue);
      }
    });
  }

  private finalizeAppStartIfNeeded(): void {
    if (!this.appStartSpan) {
      this.appStartFinalizePending = true;
      return;
    }

    const activeAppStartSpan: Span | null = this.appStartSpan;
    const appStartMetric: PerformanceMetric | null = this.appStartMetric;

    if (!activeAppStartSpan) {
      return;
    }

    this.storeFallbackParentContext(activeAppStartSpan);

    if (appStartMetric) {
      const attributes: Attributes = this.buildSpanAttributes(appStartMetric);
      this.applyAttributes(activeAppStartSpan, attributes);
      const traceId: string | undefined = (appStartMetric.attributes as { trace_id?: string }).trace_id;
      this.setTraceIdAttribute(activeAppStartSpan, traceId);
    }

    this.endSpan(activeAppStartSpan);
  }

  private storeFallbackParentContext(span: Span | null): void {
    if (!span) {
      return;
    }

    try {
      this.fallbackParentContext = span.spanContext();
    } catch {
      this.fallbackParentContext = null;
    }
  }

  /**
   * Создает SpanContext из traceId и spanId
   * Это позволяет создавать родительский контекст для дочерних спанов до создания самого родительского спана
   */
  private async createSpanContextFromIds(traceId: string, spanId: string): Promise<SpanContext | null> {
    try {
      const spanContext: SpanContext = {
        traceId,
        spanId,
        traceFlags: 0x01,
        isRemote: false,
      } as SpanContext;

      return spanContext;
    } catch (error) {
      console.warn('[Performance Metrics]: Failed to create span context from IDs', error);
      return null;
    }
  }

  private async setSpanContextTraceId(span: Span, traceId: string): Promise<void> {
    try {
      const extendedSpan: ExtendedSpan = span as ExtendedSpan;
      const currentContext: SpanContext = span.spanContext();

      if (extendedSpan.spanContext && typeof extendedSpan.spanContext === 'function') {
        if (currentContext.traceId !== traceId) {
          if (extendedSpan._spanContext) {
            extendedSpan._spanContext.traceId = traceId;
          } else {
            console.warn('[Performance Metrics] setSpanContextTraceId: _spanContext not available');
          }
        }
      } else {
        console.warn('[Performance Metrics] setSpanContextTraceId: spanContext method not available');
      }
    } catch (error: unknown) {
      console.warn('[Performance Metrics]: Failed to set traceId in span context', { error });
    }
  }

  /**
   * Устанавливает traceId и spanId в существующий спан
   * Это позволяет синхронизировать предварительно созданные ID с реальным спаном
   */
  private async setSpanContextFromIds(span: Span, traceId: string, spanId: string): Promise<void> {
    try {
      const extendedSpan: ExtendedSpan = span as ExtendedSpan;
      if (extendedSpan.spanContext && typeof extendedSpan.spanContext === 'function') {
        const currentContext: SpanContext = span.spanContext();
        if (currentContext.traceId !== traceId || currentContext.spanId !== spanId) {
          if (extendedSpan._spanContext) {
            extendedSpan._spanContext.traceId = traceId;
            extendedSpan._spanContext.spanId = spanId;
          }
        }
      }
    } catch (error: unknown) {}
  }

  private validateRootSpanIndependence(span: Span, spanName: string, expectedTraceId?: string): void {
    try {
      const spanContext: SpanContext = span.spanContext();
      const traceId: string = spanContext.traceId;

      const hasParentSpanId: boolean = 'parentSpanId' in spanContext && spanContext.parentSpanId !== undefined;

      if (hasParentSpanId) {
        console.warn('[Performance Metrics] validateRootSpanIndependence: Root span has parentSpanId', {
          spanName,
          traceId,
          parentSpanId: (spanContext as unknown as { parentSpanId?: string }).parentSpanId,
        });
      }

      if (expectedTraceId && traceId !== expectedTraceId) {
        console.warn('[Performance Metrics] validateRootSpanIndependence: TraceId mismatch', {
          spanName,
          expectedTraceId,
          actualTraceId: traceId,
        });
      }

      if (this.appStartTraceId && traceId === this.appStartTraceId && spanName !== 'app_start.cold_start') {
        console.warn('[Performance Metrics] validateRootSpanIndependence: TraceId matches app_start traceId', {
          spanName,
          traceId,
          appStartTraceId: this.appStartTraceId,
        });
      }
    } catch (error: unknown) {
      console.warn('[Performance Metrics] validateRootSpanIndependence: Failed to validate root span independence', {
        spanName,
        error,
      });
    }
  }
}
